import { useSelector } from "react-redux";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Signin from "./pages/Auth/Signin";
import VerifyOtp from "./pages/Auth/VerifyOtp";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import PasswordReset from "./pages/Auth/PasswordReset";
import { useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Admin/Dashboard";
import ScrollToTop from "./components/ScrollToTop";
import { Menu } from "lucide-react";
import MobileSidebar from "./components/MobileSidebar";
import { AnimatePresence } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import { useMemo } from "react";
import Brands from "./pages/Admin/Brands";
import CreateBrand from "./pages/Admin/CreateBrand";
import Deals from "./pages/Admin/Deals";
import Senders from "./pages/Admin/Senders";
import Support from "./pages/Admin/Support";
import ProtectedRoute from "./components/ProtectedRoute";
import "react-toastify/dist/ReactToastify.css";
import Categories from "./pages/Admin/Categories";
import Campaigns from "./pages/Admin/Campaigns";
import PremiumDeals from "./pages/Admin/PremiumDeals";
import BlastPremiumDeal from "./pages/Admin/BlastPremiumDeal";
import ViewPremiumDeal from "./pages/Admin/ViewPremiumDeal";
import PremiumDealRedemptions from "./pages/Admin/PremiumDealRedemptions";
import CreatePremiumDeal from "./pages/Admin/CreatePremiumDeal";
import Subscribers from "./pages/Admin/Subscribers";
import Promoters from "./pages/Admin/Promoters";
import PromoterApproval from "./pages/Admin/PromoterApproval";
import PayoutRequests from "./pages/Admin/PayoutRequests";
import PotentialPublishers from "./pages/Admin/PotentialPublishers";
import PotentialMerchants from "./pages/Admin/PotentialMerchants";
import PublisherConversionAnalytics from "./pages/Admin/PublisherConversionAnalytics";
import PromoSubmissions from "./pages/Admin/PromoSubmissions";
import TrashedDeals from "./pages/Admin/TrashedDeals";
import Orders from "./pages/Admin/Orders";
import Reviews from "./pages/Admin/Reviews";
import Payments from "./pages/Admin/Payments";

// Global ResizeObserver error suppression
const suppressResizeObserverErrors = () => {
  // Suppress console errors
  const originalError = console.error;
  const originalWarn = console.warn;
  
  console.error = (...args) => {
    if (
      args[0] &&
      typeof args[0] === 'string' &&
      args[0].includes('ResizeObserver loop')
    ) {
      return; // Suppress ResizeObserver errors
    }
    originalError.apply(console, args);
  };

  console.warn = (...args) => {
    if (
      args[0] &&
      typeof args[0] === 'string' &&
      args[0].includes('ResizeObserver loop')
    ) {
      return; // Suppress ResizeObserver warnings
    }
    originalWarn.apply(console, args);
  };

  // Handle window errors
  const handleWindowError = (event) => {
    if (
      event.message &&
      event.message.includes('ResizeObserver loop')
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  };

  // Handle unhandled promise rejections
  const handleUnhandledRejection = (event) => {
    if (
      event.reason &&
      event.reason.message &&
      event.reason.message.includes('ResizeObserver loop')
    ) {
      event.preventDefault();
      return false;
    }
  };

  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

  return () => {
    console.error = originalError;
    console.warn = originalWarn;
    window.removeEventListener('error', handleWindowError, true);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
  };
};

function App() {
  const user = useSelector((state) => state.user.user);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [premiumDeal, setPremiumDeal] = useState(null);

  // Apply global ResizeObserver error suppression
  useEffect(() => {
    const cleanup = suppressResizeObserverErrors();
    return cleanup;
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const navigate = useNavigate();
  const protectedRoutes = useMemo(() => ["/", "/verify-otp", "/forgot-password", "/password-reset"], []);

  
  useEffect(()=>{
    if(!user && !protectedRoutes.includes(window.location.pathname)){
      navigate("/signin");
    }
  },[navigate, user, protectedRoutes]);




  if (!user) {
    return (
      <div className="">
        <ToastContainer
          position="top-center"
          autoClose={2000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          pauseOnFocusLoss={false}
          draggable
          theme="colored"
          pauseOnHover={false}
        />
        <Routes>
          <Route path="/signin" element={<Signin />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        pauseOnFocusLoss={false}
        draggable
        theme="colored"
        pauseOnHover={false}
      />
      <ScrollToTop />
      <AnimatePresence>
      </AnimatePresence>
      <div className="flex h-screen">
        {!isMobile && <Sidebar className="flex-shrink-0 w-64" />}
        <div className={`flex-1 overflow-auto ${!isMobile ? "ml-64" : "ml-0"}`}>
          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="fixed top-4 left-4 z-40 p-2 bg-white rounded-md shadow-md"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}
          <AnimatePresence>
            {isMobile && (
              <MobileSidebar
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
              />
            )}
          </AnimatePresence>
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/brands" element={
              <ProtectedRoute>
                <Brands/>
              </ProtectedRoute>
            } />
            <Route path="/create-brand" element={
              <ProtectedRoute>
                <CreateBrand />
              </ProtectedRoute>
            } />
            <Route path="/deals" element={
              <ProtectedRoute>
                <Deals />
              </ProtectedRoute>
            } />
            <Route path="/categories" element={
              <ProtectedRoute>
                <Categories />
              </ProtectedRoute>
            } />
            <Route path="/campaigns" element={
              <ProtectedRoute>
                <Campaigns />
              </ProtectedRoute>
            } />
            <Route path="/senders" element={
              <ProtectedRoute>
                <Senders />
              </ProtectedRoute>
            } />
            <Route path="/help-and-support" element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            } />
            <Route path="/premium-deals" element={
              <ProtectedRoute>
                <PremiumDeals setPremiumDeal={setPremiumDeal} />
              </ProtectedRoute>
            } />
            <Route path="/premium-deals/:id/blast" element={
              <ProtectedRoute>
                <BlastPremiumDeal premiumDeal={premiumDeal} />
              </ProtectedRoute>
            } />
            <Route path="/premium-deals/:slug" element={
              <ProtectedRoute>
                <ViewPremiumDeal premiumDeal={premiumDeal} />
              </ProtectedRoute>
            } />
            <Route path="/premium-deals/:id/redemptions" element={
              <ProtectedRoute>
                <PremiumDealRedemptions />
              </ProtectedRoute>
            } />
            <Route path="/create-premium-deal" element={
              <ProtectedRoute>
                <CreatePremiumDeal />
              </ProtectedRoute>
            } />
            <Route path="/subscribers" element={
              <ProtectedRoute>
                <Subscribers/>
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            } />
            <Route path="/reviews" element={
              <ProtectedRoute>
                <Reviews />
              </ProtectedRoute>
            } />
            <Route path="/payments" element={
              <ProtectedRoute>
                <Payments />
              </ProtectedRoute>
            } />
            <Route path="/promoters" element={
              <ProtectedRoute>
                <Promoters />
              </ProtectedRoute>
            } />
            <Route path="/promoters/:affiliateCode/approve" element={
              <ProtectedRoute>
                <PromoterApproval />
              </ProtectedRoute>
            } />
            <Route path="/payout-requests" element={
              <ProtectedRoute>
                <PayoutRequests />
              </ProtectedRoute>
            } />
            <Route path="/potential-publishers" element={
              <ProtectedRoute>
                <PotentialPublishers />
              </ProtectedRoute>
            } />
            <Route path="/potential-merchants" element={
              <ProtectedRoute>
                <PotentialMerchants />
              </ProtectedRoute>
            } />
            <Route path="/publisher-conversion-analytics" element={
              <ProtectedRoute>
                <PublisherConversionAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/promo-submissions" element={
              <ProtectedRoute>
                <PromoSubmissions />
              </ProtectedRoute>
            } />
            <Route path="/deals/trash" element={
              <ProtectedRoute>
                <TrashedDeals />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
